import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useInventories, usePurchaseOrders, useSalesOrders } from "@/hooks";
import { Inventory, PurchaseOrder, QueryConstraint, SalesOrder } from "@/core";
import { getInventoryOperationalStatus } from "@/components/operations/operations-shared";
import { formatCurrency, formatNumber } from "@/utils/formatting";
import { getPurchaseOrderTotal, getSalesOrderTotal } from "@/utils/order-total";
import {
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  subMonths,
  subYears,
  subQuarters,
  startOfDay,
  endOfDay,
  format,
} from "date-fns";
import {
  DollarSign,
  ShoppingCart,
  Package,
  CalendarDays,
  RefreshCw,
  Info,
  Plane,
  TrendingUp,
  Layers,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";

// ─── Period Preset ────────────────────────────────────────────────────────────

type PeriodPreset =
  | "mtd"
  | "last_month"
  | "qtd"
  | "last_quarter"
  | "ytd"
  | "last_year"
  | "all"
  | "custom";

const PERIOD_BUTTONS: { value: PeriodPreset; label: string }[] = [
  { value: "mtd", label: "MTD" },
  { value: "last_month", label: "Last Mo." },
  { value: "qtd", label: "QTD" },
  { value: "last_quarter", label: "Last Q." },
  { value: "ytd", label: "YTD" },
  { value: "last_year", label: "Last Yr." },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom" },
];

function getPeriodRange(
  preset: PeriodPreset,
  customRange?: DateRange,
): { from: Date | undefined; to: Date | undefined } {
  const now = new Date();
  const today = startOfDay(now);
  switch (preset) {
    case "mtd":
      return { from: startOfMonth(today), to: endOfDay(now) };
    case "last_month": {
      const lm = subMonths(today, 1);
      return { from: startOfMonth(lm), to: endOfMonth(lm) };
    }
    case "qtd":
      return { from: startOfQuarter(today), to: endOfDay(now) };
    case "last_quarter": {
      const lq = subQuarters(today, 1);
      return { from: startOfQuarter(lq), to: endOfQuarter(lq) };
    }
    case "ytd":
      return { from: startOfYear(today), to: endOfDay(now) };
    case "last_year": {
      const ly = subYears(today, 1);
      return { from: startOfYear(ly), to: endOfYear(ly) };
    }
    case "custom":
      return { from: customRange?.from, to: customRange?.to };
    case "all":
    default:
      return { from: undefined, to: undefined };
  }
}

function buildDateConstraints<T>(
  from: Date | undefined,
  to: Date | undefined,
  field: keyof T & string,
): QueryConstraint<keyof T & string>[] {
  const c: QueryConstraint<keyof T & string>[] = [];
  if (from) c.push({ field, operator: ">=", value: from });
  if (to) c.push({ field, operator: "<=", value: to });
  return c;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function OperationsReportsPage() {
  const [preset, setPreset] = useState<PeriodPreset>("mtd");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const periodRange = useMemo(
    () => getPeriodRange(preset, customRange),
    [preset, customRange],
  );

  const periodLabel = useMemo(() => {
    if (!periodRange.from && !periodRange.to) return "All time — no date filter applied";
    const fmt = (d: Date) => format(d, "MMM d, yyyy");
    if (periodRange.from && periodRange.to)
      return `${fmt(periodRange.from)} – ${fmt(periodRange.to)}`;
    if (periodRange.from) return `From ${fmt(periodRange.from)}`;
    return "";
  }, [periodRange]);

  // ─── Queries ──────────────────────────────────────────────────────────────

  const soConstraints = buildDateConstraints<SalesOrder>(
    periodRange.from,
    periodRange.to,
    "createdAt",
  );
  const salesOrdersQuery = useSalesOrders({
    pagination: { limit: 500 },
    queryConstraints: soConstraints,
    orderBy: { field: "createdAt", direction: "desc" },
  });

  const poConstraints = buildDateConstraints<PurchaseOrder>(
    periodRange.from,
    periodRange.to,
    "createdAt",
  );
  const purchaseOrdersQuery = usePurchaseOrders({
    pagination: { limit: 500 },
    queryConstraints: poConstraints,
    orderBy: { field: "createdAt", direction: "desc" },
  });

  const inventoriesQuery = useInventories({
    pagination: { limit: 500 },
    orderBy: { field: "updatedAt", direction: "desc" },
  });

  const inventoryConstraints = buildDateConstraints<Inventory>(
    periodRange.from,
    periodRange.to,
    "createdAt",
  );
  const periodInventoriesQuery = useInventories({
    pagination: { limit: 500 },
    queryConstraints: inventoryConstraints,
    orderBy: { field: "createdAt", direction: "desc" },
  });

  const salesOrders = useMemo(
    () => salesOrdersQuery.data?.pages.flat() ?? [],
    [salesOrdersQuery.data],
  );
  const purchaseOrders = useMemo(
    () => purchaseOrdersQuery.data?.pages.flat() ?? [],
    [purchaseOrdersQuery.data],
  );
  const allInventories = useMemo(
    () => inventoriesQuery.data?.pages.flat() ?? [],
    [inventoriesQuery.data],
  );
  const periodInventories = useMemo(
    () => periodInventoriesQuery.data?.pages.flat() ?? [],
    [periodInventoriesQuery.data],
  );

  const isLoading =
    salesOrdersQuery.isLoading ||
    purchaseOrdersQuery.isLoading ||
    inventoriesQuery.isLoading ||
    periodInventoriesQuery.isLoading;
  const isFetching =
    salesOrdersQuery.isFetching ||
    purchaseOrdersQuery.isFetching ||
    inventoriesQuery.isFetching ||
    periodInventoriesQuery.isFetching;

  // ─── Metrics ──────────────────────────────────────────────────────────────

  const metrics = useMemo(() => {
    // Period: Sales
    const revenue = salesOrders.reduce(
      (s, o) =>
        s +
        getSalesOrderTotal({
          revenue: o.revenue,
          taxCost: o.taxCost,
          totalAmount: o.totalAmount,
        }),
      0,
    );
    const milesSold = salesOrders.reduce((s, o) => s + o.totalMilesSold, 0);
    const orderCount = salesOrders.length;
    const avgSellCpm = milesSold > 0 ? (revenue / milesSold) * 1000 : 0;

    // Period: Purchases
    const purchaseSpend = purchaseOrders.reduce(
      (s, po) =>
        s +
        getPurchaseOrderTotal({
          buyPrice: po.buyPrice,
          taxCost: po.taxCost,
          totalAmount: po.totalAmount,
        }),
      0,
    );
    const milesBoughtPo = purchaseOrders.reduce(
      (s, po) => s + po.amountBought,
      0,
    );
    const poCount = purchaseOrders.length;
    const avgBuyCpmPo =
      poCount > 0
        ? purchaseOrders.reduce((s, po) => s + po.buyCpm, 0) / poCount
        : 0;

    // Spread (sell minus buy from period POs)
    const spread =
      avgSellCpm > 0 && avgBuyCpmPo > 0 ? avgSellCpm - avgBuyCpmPo : 0;

    // Inventory health (all inventories — cumulative/all-time)
    const totalAvailableMiles = allInventories.reduce(
      (sum, inventory) => sum + inventory.availableMiles,
      0,
    );
    const totalPurchasedMiles = allInventories.reduce(
      (sum, inventory) => sum + inventory.totalPurchasedMiles,
      0,
    );
    const totalSoldMiles = allInventories.reduce(
      (sum, inventory) => sum + inventory.totalSoldMiles,
      0,
    );
    const sellThroughRate =
      totalPurchasedMiles > 0 ? totalSoldMiles / totalPurchasedMiles : 0;
    const activeInventories = allInventories.filter(
      (inventory) =>
        !["closed", "blocked"].includes(
          getInventoryOperationalStatus({ inventory }),
        ),
    ).length;
    const totalInventories = allInventories.length;
    const blockedInventories = allInventories.filter(
      (inventory) =>
        getInventoryOperationalStatus({ inventory }) === "blocked" ||
        ["restricted", "closed"].includes(inventory.accountStatus),
    ).length;

    // Period: New inventories
    const newInventories = periodInventories.length;
    const newAvailableMiles = periodInventories.reduce(
      (sum, inventory) => sum + inventory.availableMiles,
      0,
    );

    return {
      revenue,
      milesSold,
      orderCount,
      avgSellCpm,
      purchaseSpend,
      milesBoughtPo,
      poCount,
      avgBuyCpmPo,
      spread,
      totalAvailableMiles,
      totalPurchasedMiles,
      totalSoldMiles,
      sellThroughRate,
      activeInventories,
      totalInventories,
      blockedInventories,
      newInventories,
      newAvailableMiles,
    };
  }, [salesOrders, purchaseOrders, allInventories, periodInventories]);

  function handleRefresh() {
    salesOrdersQuery.refetch();
    purchaseOrdersQuery.refetch();
    inventoriesQuery.refetch();
    periodInventoriesQuery.refetch();
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <TooltipProvider>
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-7">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Plane className="h-4 w-4" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">
                Operations Report
              </h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 ml-10">
              Realized profitability and inventory health dashboard
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading || isFetching}
            className="gap-2 self-start sm:self-auto"
          >
            <RefreshCw
              className={cn(
                "h-4 w-4",
                (isLoading || isFetching) && "animate-spin",
              )}
            />
            Refresh
          </Button>
        </div>

        {/* Period Selector */}
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-1 flex-wrap">
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground mr-1 shrink-0" />
                {PERIOD_BUTTONS.map((opt) =>
                  opt.value === "custom" ? (
                    <Popover
                      key="custom"
                      open={calendarOpen}
                      onOpenChange={(open) => {
                        setCalendarOpen(open);
                        if (open) setPreset("custom");
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant={preset === "custom" ? "default" : "ghost"}
                          size="sm"
                          className="h-7 px-3 text-xs"
                        >
                          {opt.label}
                          {preset === "custom" &&
                            customRange?.from &&
                            customRange?.to && (
                              <span className="ml-1 opacity-70">
                                {format(customRange.from, "MMM d")}–
                                {format(customRange.to, "MMM d")}
                              </span>
                            )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="range"
                          selected={customRange}
                          onSelect={(range) => {
                            setCustomRange(range);
                            if (range?.from && range?.to)
                              setCalendarOpen(false);
                          }}
                          numberOfMonths={2}
                          disabled={(d) => d > new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Button
                      key={opt.value}
                      variant={preset === opt.value ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setPreset(opt.value)}
                      className="h-7 px-3 text-xs"
                    >
                      {opt.label}
                    </Button>
                  ),
                )}
              </div>
              <p className="text-xs text-muted-foreground font-mono shrink-0">
                {periodLabel}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section: Sales Performance */}
        <section className="space-y-3">
          <SectionHeader
            label="Sales Performance"
            sublabel="filtered by selected period"
          />
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Revenue"
              value={formatCurrency(metrics.revenue)}
              icon={<DollarSign className="h-3.5 w-3.5" />}
              loading={isLoading}
              highlight
            />
            <KpiCard
              title="Sales Orders"
              value={formatNumber(metrics.orderCount)}
              icon={<ShoppingCart className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Miles Sold"
              value={formatNumber(Math.round(metrics.milesSold))}
              icon={<Plane className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Avg Sell CPM"
              value={
                metrics.avgSellCpm > 0
                  ? `${metrics.avgSellCpm.toFixed(4)}¢`
                  : "—"
              }
              icon={<TrendingUp className="h-3.5 w-3.5" />}
              loading={isLoading}
              positive={metrics.avgSellCpm > 0}
            />
          </div>
        </section>

        {/* Section: Purchase Activity */}
        <section className="space-y-3">
          <SectionHeader
            label="Purchase Activity"
            sublabel="filtered by selected period"
          />
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Capital Deployed"
              value={formatCurrency(metrics.purchaseSpend)}
              icon={<DollarSign className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Purchase Orders"
              value={formatNumber(metrics.poCount)}
              icon={<Package className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Miles Acquired"
              value={formatNumber(Math.round(metrics.milesBoughtPo))}
              icon={<Plane className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Avg Buy CPM"
              value={
                metrics.avgBuyCpmPo > 0
                  ? `${metrics.avgBuyCpmPo.toFixed(5)}¢`
                  : "—"
              }
              icon={<TrendingUp className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
          </div>
        </section>

        {/* CPM Spread Banner — only shown when both CPMs are available */}
        {metrics.spread !== 0 && (
          <Card
            className={cn(
              "border",
              metrics.spread > 0
                ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30"
                : "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30",
            )}
          >
            <CardContent className="py-4 px-5">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="space-y-0.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    CPM Spread · Sell minus Buy
                  </p>
                  <div className="flex items-center gap-2">
                    {metrics.spread > 0 ? (
                      <ArrowUpRight className="h-5 w-5 text-green-600" />
                    ) : (
                      <ArrowDownRight className="h-5 w-5 text-red-600" />
                    )}
                    <p
                      className={cn(
                        "text-2xl font-mono font-bold",
                        metrics.spread > 0 ? "text-green-600" : "text-red-600",
                      )}
                    >
                      {metrics.spread > 0 ? "+" : ""}
                      {metrics.spread.toFixed(4)}¢ / mi
                    </p>
                  </div>
                </div>
                <div className="flex gap-8 divide-x divide-border">
                  <div className="text-right pr-8">
                    <p className="text-xs text-muted-foreground">Avg Sell CPM</p>
                    <p className="text-base font-mono font-semibold">
                      {metrics.avgSellCpm.toFixed(4)}¢
                    </p>
                  </div>
                  <div className="text-right pl-8">
                    <p className="text-xs text-muted-foreground">Avg Buy CPM</p>
                    <p className="text-base font-mono font-semibold">
                      {metrics.avgBuyCpmPo.toFixed(5)}¢
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Inventory Health */}
        <section className="space-y-3">
          <SectionHeader
            label="Inventory Health"
            sublabel="all-time cumulative"
            tooltip="These figures reflect the current state of inventory aggregates across operations, using the same balances that power the inventory workspace."
          />
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Available Miles"
              value={formatNumber(Math.round(metrics.totalAvailableMiles))}
              icon={<Plane className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Purchased Miles"
              value={formatNumber(Math.round(metrics.totalPurchasedMiles))}
              icon={<Package className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Sold Miles"
              value={formatNumber(Math.round(metrics.totalSoldMiles))}
              icon={<BarChart3 className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Sell-Through Rate"
              value={`${(metrics.sellThroughRate * 100).toFixed(2)}%`}
              icon={<TrendingUp className="h-3.5 w-3.5" />}
              loading={isLoading}
              positive={metrics.sellThroughRate > 0}
            />
          </div>
        </section>

        {/* Section: Inventory Overview */}
        <section className="space-y-3">
          <SectionHeader
            label="Inventory Overview"
            sublabel="inventory aggregates · period creations"
            tooltip="'Blocked Inventories' includes blocked inventory records and inventories whose account status is restricted or closed. Period metrics are based on inventory creation date."
          />
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Active Inventories"
              value={formatNumber(metrics.activeInventories)}
              icon={<Layers className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Total Inventories"
              value={formatNumber(metrics.totalInventories)}
              icon={<Layers className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="Blocked Inventories"
              value={formatNumber(metrics.blockedInventories)}
              icon={<Package className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="New Inventories (Period)"
              value={formatNumber(metrics.newInventories)}
              icon={<Package className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
            <KpiCard
              title="New Available Miles (Period)"
              value={formatNumber(Math.round(metrics.newAvailableMiles))}
              icon={<Plane className="h-3.5 w-3.5" />}
              loading={isLoading}
            />
          </div>
        </section>
      </div>
    </TooltipProvider>
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────────

function SectionHeader({
  label,
  sublabel,
  tooltip,
}: {
  label: string;
  sublabel?: string;
  tooltip?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </h2>
      {sublabel && (
        <span className="text-xs text-muted-foreground/60">· {sublabel}</span>
      )}
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// ─── KpiCard ─────────────────────────────────────────────────────────────────

function KpiCard({
  title,
  value,
  icon,
  loading,
  highlight,
  positive,
  negative,
}: {
  title: string;
  value: string;
  icon: ReactNode;
  loading?: boolean;
  highlight?: boolean;
  positive?: boolean;
  negative?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 flex flex-col gap-3 transition-colors",
        highlight &&
          "border-primary/30 bg-primary/[0.03] dark:bg-primary/[0.06]",
      )}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground leading-none">
          {title}
        </p>
        <span
          className={cn(
            "text-muted-foreground",
            highlight && "text-primary",
          )}
        >
          {icon}
        </span>
      </div>
      {loading ? (
        <div className="h-7 w-28 rounded-md bg-muted animate-pulse" />
      ) : (
        <p
          className={cn(
            "text-xl font-mono font-semibold tracking-tight tabular-nums",
            positive && !negative && "text-green-600 dark:text-green-500",
            negative && !positive && "text-red-600 dark:text-red-500",
            highlight && !positive && !negative && "text-primary",
          )}
        >
          {value}
        </p>
      )}
    </div>
  );
}
